'use client';
import classnames from 'classnames';
import { useState } from 'react';

import styles from 'molecules/Form/textinput/textinput.module.scss';

import { normalizePhoneInput } from 'utils/functions';

import type { ChangeEvent, FC } from 'react';

interface TextInputProps {
  className?: string;
  disabled?: boolean;
  formStateProps?: {
    checked?: boolean;
    multiple?: boolean;
    name?: string;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    type?: string;
    value?: string;
  };
  helpText?: string;
  hidden?: boolean;
  id: string; // required for accessibility + field ID
  label: string; // visible label
  maxLength?: number;
  message?: string; // error message
  pattern?: string;
  patternMessage?: string;
  required?: boolean;
  type?: string;
  valid?: boolean;
  validate?: (fieldName: string, value: string, required: boolean) => void;
  validateOnBlur?: (fieldName: string, value: string, required: boolean) => void;
  validateOnChange?: (fieldName: string, value: string, required: boolean) => void;
}
const TextInput: FC<TextInputProps> = ({
  className,
  disabled,
  formStateProps = {},
  helpText,
  hidden,
  id,
  label,
  maxLength,
  message = 'This field is required.',
  pattern,
  patternMessage,
  required = false,
  type,
  valid = true,
  validateOnBlur,
  validateOnChange,
}) => {
  const [value, setValue] = useState<string>('');
  // Destructure react-use-form-state props (external controlled form)
  const { onChange: outerOnChange, value: formStateValue, ...passFormStateProps } = formStateProps;
  // Generate wrapper classes conditionally
  const wrapperClass = classnames(styles.wrapper, {
    [styles.floated]: value || formStateValue !== '' ? true : false,
    [styles.error]: !valid,
    [styles.required]: required,
    [styles.disabled]: disabled,
    [className || '']: className,
  });

  // Input change handler — phone sanitization + validation trigger
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;
    // If it's the phone field, sanitize it
    if (id === 'phone') {
      const cleaned = normalizePhoneInput(inputValue);

      if (cleaned === null) {
        return; // Do not update
      }

      inputValue = cleaned;
    }

    setValue(inputValue); // internal component state
    // Trigger debounced or immediate validation (from props)
    validateOnChange?.(id, inputValue, required);
    // Forward event to form library (like react-use-form-state)
    if (outerOnChange) {
      outerOnChange({
        ...e,
        target: {
          ...e.target,
          value: inputValue,
        },
      });
    }
  };
  // Input blur handler — trigger validation on blur
  const handleBlur = () => {
    validateOnBlur?.(id, value, required);
  };

  return (
    <div className={wrapperClass}>
      <label htmlFor={id} id={helpText ? `labelledby-label-${id}` : undefined} className={styles.label}>
        {label}
      </label>
      <input
        {...passFormStateProps}
        aria-describedby={!valid ? `error-${id}` : undefined}
        aria-labelledby={helpText ? `labelledby-label-${id} labelledby-help-${id}` : undefined}
        aria-invalid={!valid}
        aria-required={required}
        className={styles.input}
        disabled={disabled}
        maxLength={maxLength || undefined}
        id={id}
        onBlur={handleBlur}
        onChange={handleChange}
        pattern={pattern || undefined}
        title={pattern && patternMessage ? patternMessage : undefined}
        type={type || passFormStateProps.type || 'text'}
        value={formStateValue !== undefined ? formStateValue : value} // safely fallback to local state
        hidden={hidden || undefined}
      />
      {/* Inline error message */}
      {message && valid === false && (
        <div id={`error-${id}`} className={styles.errorMessage} aria-hidden={valid}>
          {message}
        </div>
      )}
      {/* Optional help text below input */}
      {helpText && (
        <span className={styles.helpText} id={`labelledby-help-${id}`}>
          {helpText}
        </span>
      )}
    </div>
  );
};
export default TextInput;
